import { Trash, Plus, Minus } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { fmtEUR } from "@/lib/api";

export default function CartTable({ cart, onRemove, onUpdateQty, onSetQty, totals }) {
    return (
        <div className="bg-white border-2 border-[#0A0A0A] flex flex-col h-full min-h-0">
            {/* Header row */}
            <div className="grid grid-cols-[32px_1fr_104px_70px_104px_36px] bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-[0.2em]">
                <div className="px-1 py-3 text-center">Α/Α</div>
                <div className="px-3 py-3">ΠΕΡΙΓΡΑΦΗ</div>
                <div className="px-2 py-3 text-center">ΠΟΣΟΤΗΤΑ</div>
                <div className="px-2 py-3 text-right">ΤΙΜΗ</div>
                <div className="px-2 py-3 text-right">ΣΥΝΟΛΟ</div>
                <div className="px-1 py-3" />
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
                {cart.length === 0 ? (
                    <div className="p-12 text-center text-sm text-[#5C5F66]">
                        Το καλάθι είναι κενό. Επιλέξτε είδος από τα δεξιά.
                    </div>
                ) : (
                    cart.map(({ item, quantity }, idx) => (
                        <div
                            key={item.id}
                            data-testid={`cart-row-${item.code}`}
                            className="grid grid-cols-[32px_1fr_104px_70px_104px_36px] items-center border-b border-[#E2E4E9] hover:bg-[#F4F5F7]"
                        >
                            <div className="px-1 py-3 text-center font-mono text-sm text-[#5C5F66]">
                                {idx + 1}
                            </div>
                            <div className="px-3 py-3 min-w-0">
                                <div className="font-bold text-sm text-[#0A0A0A] truncate">
                                    {item.description}
                                </div>
                                <div className="text-[10px] text-[#5C5F66] font-mono">
                                    {item.code} · ΦΠΑ {item.vat_rate}%
                                </div>
                            </div>
                            <div className="px-2 py-3 flex items-center justify-center gap-0.5">
                                <button
                                    onClick={() => onUpdateQty(item.id, -1)}
                                    data-testid={`cart-qty-minus-${item.code}`}
                                    className="w-8 h-9 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center justify-center active:scale-95"
                                >
                                    <Minus size={12} weight="bold" />
                                </button>
                                <Input
                                    value={quantity}
                                    onChange={(e) => onSetQty(item.id, e.target.value)}
                                    inputMode="decimal"
                                    data-testid={`cart-qty-input-${item.code}`}
                                    className="rounded-none h-9 w-10 text-center font-mono font-bold text-sm border-[#E2E4E9] px-0"
                                />
                                <button
                                    onClick={() => onUpdateQty(item.id, +1)}
                                    data-testid={`cart-qty-plus-${item.code}`}
                                    className="w-8 h-9 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center justify-center active:scale-95"
                                >
                                    <Plus size={12} weight="bold" />
                                </button>
                            </div>
                            <div className="px-2 py-3 text-right font-mono text-sm">
                                {fmtEUR(item.unit_price)}
                            </div>
                            <div className="px-2 py-3 text-right font-mono font-bold text-base">
                                {fmtEUR(item.unit_price * quantity)}
                            </div>
                            <div className="px-1 py-3 flex justify-center">
                                <button
                                    onClick={() => onRemove(item.id)}
                                    data-testid={`remove-${item.code}`}
                                    className="p-1 text-[#FF3B30] hover:bg-[#FDEAE8]"
                                >
                                    <Trash size={14} weight="bold" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer totals */}
            <div className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA] px-5 py-4 space-y-1.5">
                {Object.entries(totals.breakdown).length > 0 && (
                    <div className="space-y-1 pb-2 mb-2 border-b border-[#E2E4E9]">
                        {Object.entries(totals.breakdown)
                            .sort()
                            .map(([rate, b]) => (
                                <div
                                    key={rate}
                                    className="flex justify-between text-[11px] font-mono text-[#5C5F66]"
                                >
                                    <span>ΦΠΑ {rate}%</span>
                                    <span>
                                        Καθ. {fmtEUR(b.net)} · ΦΠΑ {fmtEUR(b.vat)} · {fmtEUR(b.gross)}
                                    </span>
                                </div>
                            ))}
                    </div>
                )}
                <div className="flex justify-between text-sm font-mono">
                    <span className="text-[#5C5F66] uppercase tracking-wider text-xs">ΚΑΘΑΡΗ ΑΞΙΑ</span>
                    <span className="font-bold">{fmtEUR(totals.net)}</span>
                </div>
                <div className="flex justify-between text-sm font-mono">
                    <span className="text-[#5C5F66] uppercase tracking-wider text-xs">ΦΠΑ</span>
                    <span className="font-bold">{fmtEUR(totals.vat)}</span>
                </div>
                <div className="flex justify-between items-end pt-2 border-t border-[#0A0A0A]">
                    <span className="text-base font-bold uppercase tracking-wider">ΣΥΝΟΛΟ</span>
                    <span
                        data-testid="cart-total"
                        className="font-heading text-4xl font-black tracking-tighter text-[#002FA7]"
                    >
                        {fmtEUR(totals.gross)}
                    </span>
                </div>
            </div>
        </div>
    );
}
